import { Combobox } from "@dashboard/components/Combobox";
import {
  useWsmFitmentProductAxisValuesQuery,
  WsmFitmentProductAxisFragment,
} from "@dashboard/graphql";
import { Option } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { fitmentMessages } from "./messages";
import { compareLabels } from "./utils";

export interface AxisValueSelectProps {
  axis: WsmFitmentProductAxisFragment;
  value: Option | null;
  disabled?: boolean;
  onChange: (value: Option | null) => void;
}

/**
 * One value on one axis. The typed text goes to the server as `search`
 * (the Combobox debounces it); the un-searched list pages on scroll.
 */
export const AxisValueSelect = ({ axis, value, disabled, onChange }: AxisValueSelectProps) => {
  const intl = useIntl();
  const [search, setSearch] = React.useState("");
  // The search the query is on now, read when a page lands (see onFetchMore).
  const searchRef = React.useRef(search);

  searchRef.current = search;

  const { data, loading, fetchMore } = useWsmFitmentProductAxisValuesQuery({
    variables: { axis: axis.id, search: search || null },
    notifyOnNetworkStatusChange: true,
    // After the first read, re-reads (the reobserve that ends every
    // fetchMore in Apollo 3.4) come from the cache, so the pages scrolled in
    // are not replaced by a fresh page 1.
    nextFetchPolicy: "cache-first",
  });
  // The server returns values in (updatedAt, id) order. The list is searched
  // on the server and paged on scroll, so this sorts only the values loaded
  // so far (numeric-aware, so a Year axis reads 2009, 2010, ...); a page that
  // arrives later is merged into that order, not appended after it.
  const options = React.useMemo<Option[]>(
    () =>
      (data?.wsmFitmentAxisValues?.edges ?? [])
        .map(edge => ({
          label: edge.node.value,
          value: edge.node.id,
        }))
        .sort((a, b) => compareLabels(a.label, b.label)),
    [data],
  );
  const pageInfo = data?.wsmFitmentAxisValues?.pageInfo;
  const onFetchMore = () => {
    // One page at a time: a scroll during a fetch would request the same
    // cursor again and append its edges twice.
    if (loading || !pageInfo?.hasNextPage) {
      return;
    }

    // updateQuery merges into whatever the query holds when the page lands:
    // a page for the previous search must not end up in the new one's list.
    const issuedFor = searchRef.current;

    return fetchMore({
      variables: { after: pageInfo?.endCursor },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult?.wsmFitmentAxisValues || searchRef.current !== issuedFor) {
          return previous;
        }

        return {
          ...fetchMoreResult,
          wsmFitmentAxisValues: {
            ...fetchMoreResult.wsmFitmentAxisValues,
            edges: [
              ...(previous.wsmFitmentAxisValues?.edges ?? []),
              ...fetchMoreResult.wsmFitmentAxisValues.edges,
            ],
          },
        };
      },
    });
  };

  return (
    <Combobox
      name={`axis-${axis.id}`}
      data-test-id={`fitment-axis-${axis.name}`}
      label={axis.displayLabel || axis.name}
      helperText={
        !loading && !search && options.length === 0
          ? intl.formatMessage(fitmentMessages.noAxisValues)
          : undefined
      }
      options={options}
      value={value}
      disabled={disabled}
      loading={loading}
      allowEmptyValue
      fetchOptions={setSearch}
      fetchMore={{
        hasMore: !!pageInfo?.hasNextPage,
        loading,
        onFetchMore,
      }}
      onChange={event => {
        const id = event.target.value as string | null;

        onChange(id ? (options.find(option => option.value === id) ?? value) : null);
      }}
    />
  );
};

export default AxisValueSelect;
